import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";

interface MatchProfile {
  id: string;
  ratingAvg: string;
  ratingCount: number;
  verificationLevel: "unverified" | "identity_verified" | "pro_verified" | "trusted_pro";
  city?: string | null;
  headline?: string | null;
  bio?: string | null;
  user: {
    displayName?: string | null;
    firstName?: string | null;
    phone?: string | null;
    telegramUsername?: string | null;
  };
  categories: Array<{ slug: string; nameRo: string; nameRu?: string | null }>;
}

interface RequestDetail {
  id: string;
  rawText: string;
  status: string;
  selectedMode: "browse" | "broadcast" | "operator";
  createdAt: string;
  city?: string | null;
  scheduledFor?: string | null;
  category?: { nameRo: string; nameRu?: string | null } | null;
  assignment?: {
    id: string;
    profile: MatchProfile | null;
  } | null;
  matches: Array<{
    id: string;
    matchScore: string;
    responseStatus: "pending" | "accepted" | "declined" | "withdrawn" | "expired";
    profile: MatchProfile | null;
  }>;
}

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(15 * 60);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const loadRequest = async () => {
      const { data } = await api.get<RequestDetail>(`/requests/${id}`);
      if (!cancelled) setRequest(data);
    };

    void loadRequest();
    const interval = window.setInterval(() => { void loadRequest(); }, 5000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [id]);

  useEffect(() => {
    if (!request || request.selectedMode !== "broadcast") return;
    const startedAt = new Date(request.createdAt).getTime();
    const windowMs = 15 * 60 * 1000;
    const timer = window.setInterval(() => {
      setRemainingSeconds(Math.max(0, Math.floor((startedAt + windowMs - Date.now()) / 1000)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [request]);

  const progress = useMemo(() => {
    if (!request) return 0;
    if (request.status === "assigned" || request.status === "completed") return 100;
    if (request.selectedMode === "browse") return 100;
    if (request.selectedMode === "operator") return 40;
    return Math.max(5, Math.min(100, ((15 * 60 - remainingSeconds) / (15 * 60)) * 100));
  }, [remainingSeconds, request]);

  const getMatchStatusLabel = (status: RequestDetail["matches"][number]["responseStatus"]) => {
    switch (status) {
      case "accepted": return t.leadStatusAccepted;
      case "declined": return t.leadStatusDeclined;
      case "withdrawn": return t.leadStatusWithdrawn;
      default: return t.leadStatusPending;
    }
  };

  const matchStatusColor = (status: RequestDetail["matches"][number]["responseStatus"]) => {
    switch (status) {
      case "accepted": return "bg-emerald-50 text-emerald-700";
      case "declined": return "bg-gray-100 text-gray-500";
      default: return "bg-orange-50 text-orange-600";
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return t.anytime;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t.anytime;
    return date.toLocaleString();
  };

  if (!request) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-sm text-gray-400">{t.loading}</div>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    assigned: "bg-emerald-50 text-emerald-700",
    completed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-gray-100 text-gray-500",
    expired: "bg-gray-100 text-gray-500",
    matching: "bg-orange-50 text-orange-600",
    pending: "bg-orange-50 text-orange-600",
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate("/")} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="flex items-start justify-between gap-3">
          <div className="text-2xl font-bold text-gray-900">{t.requestDetails}</div>
          <div className={`mt-1 rounded-full px-3 py-1 text-xs font-semibold ${statusColors[request.status] ?? "bg-gray-100 text-gray-600"}`}>
            {t.requestStatusLabels[request.status as keyof typeof t.requestStatusLabels] ?? request.status}
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-500 leading-snug">{request.rawText}</div>
      </div>

      <div className="space-y-4 p-5">
        {/* Status card */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="grid gap-1.5 text-sm text-gray-500 mb-4">
            <div>📍 {request.city || "-"}</div>
            <div>🕐 {formatDateTime(request.scheduledFor)}</div>
          </div>
          {/* Progress bar */}
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-orange-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
            <span>{request.selectedMode === "broadcast" ? t.searchingProviders : t.searchCompleted}</span>
            <span className="font-semibold text-orange-500">
              {request.selectedMode === "broadcast" ? formatCountdown(remainingSeconds) : "✓"}
            </span>
          </div>
        </div>

        {/* Assigned handyman */}
        {request.assignment?.profile ? (
          <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-emerald-500" />
              <div className="text-sm font-bold text-emerald-700">{t.assignedHandyman}</div>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {request.assignment.profile.user.displayName || request.assignment.profile.user.firstName || "Handyman"}
            </div>
            <div className="mt-0.5 text-sm text-gray-500">
              {request.assignment.profile.headline || request.assignment.profile.city || "-"}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              ★ {Number(request.assignment.profile.ratingAvg).toFixed(1)} · {request.assignment.profile.ratingCount} {t.reviewsCount}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/handymen/${request.assignment.profile.id}`} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700">
                {t.viewProfile}
              </Link>
              {request.assignment.profile.user.telegramUsername ? (
                <a href={`https://t.me/${request.assignment.profile.user.telegramUsername}`} target="_blank" rel="noreferrer" className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white">
                  {t.openTelegram}
                </a>
              ) : null}
              {request.assignment.profile.user.phone ? (
                <a href={`tel:${request.assignment.profile.user.phone}`} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700">
                  {t.callPhone}
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Matches */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-base font-bold text-gray-900">{t.foundProviders}</div>
            {request.matches.length > 0 ? (
              <div className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
                {request.matches.length}
              </div>
            ) : null}
          </div>
          {request.matches.length === 0 ? (
            <div className="rounded-xl bg-gray-50 px-4 py-6 text-center text-sm text-gray-400">{t.noMatches}</div>
          ) : (
            <div className="space-y-3">
              {request.matches.map((match) => {
                const profile = match.profile;
                if (!profile) return null;
                const name = profile.user.displayName || profile.user.firstName || "Handyman";
                const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                const categoryNames = profile.categories.map((item) => locale === "ru" ? item.nameRu || item.nameRo : item.nameRo);
                return (
                  <div key={match.id} className="rounded-xl border border-gray-100 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-sm font-bold text-orange-600">
                        {initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-semibold text-gray-900">{name}</div>
                            <div className="text-xs text-gray-500">{profile.headline || profile.city || "-"}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <div className="rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-bold text-orange-600">
                              {Math.round(Number(match.matchScore) * 100)}%
                            </div>
                            <div className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${matchStatusColor(match.responseStatus)}`}>
                              {getMatchStatusLabel(match.responseStatus)}
                            </div>
                          </div>
                        </div>
                        {categoryNames.length > 0 ? (
                          <div className="mt-2 text-xs text-gray-400">{categoryNames.join(" · ")}</div>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link to={`/handymen/${profile.id}`} className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700">
                        {t.viewProfile}
                      </Link>
                      {profile.user.telegramUsername ? (
                        <a href={`https://t.me/${profile.user.telegramUsername}`} target="_blank" rel="noreferrer" className="rounded-xl bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">
                          {t.openTelegram}
                        </a>
                      ) : null}
                      {profile.user.phone ? (
                        <a href={`tel:${profile.user.phone}`} className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700">
                          {t.callPhone}
                        </a>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
